import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EmptyState } from '@/components/custom/empty-state';
import { formatPercentage } from '../formatters';

echarts.use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const CORRECT_COLOR = '#23CA88';
const INCORRECT_COLOR = '#FF7A5C';

export function SubjectPerformanceChart({ data = [] }) {
  const [option, setOption] = useState({});

  useEffect(() => {
    if (!data.length) return;

    setOption({
      backgroundColor: 'transparent',
      grid: {
        left: 8,
        right: '5%',
        bottom: '10%',
        top: '5%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 0,
        textStyle: { color: '#666' },
        // Mostra o % pooled de acerto/erro (vindo direto da API) em vez da
        // contagem arredondada usada só pra dimensionar a barra empilhada.
        formatter: params => {
          const item = data[params[0]?.dataIndex];
          if (!item) return '';
          const row = (color, label, value) =>
            `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">` +
            `<span><span style="display:inline-block;margin-right:4px;border-radius:10px;width:9px;height:9px;background-color:${color}"></span>${label}</span>` +
            `<b>${value}</b>` +
            `</div>`;
          return [`<div style="font-weight:600;margin-bottom:4px;">${item.subject}</div>`, row(CORRECT_COLOR, 'Acerto', formatPercentage(item.accuracyPercentage)), row(INCORRECT_COLOR, 'Erro', formatPercentage(item.errorPercentage))].join('');
        }
      },
      legend: {
        show: true,
        bottom: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: '#8894AC', fontSize: 12 }
      },
      xAxis: {
        type: 'value',
        // Eixo é contagem de ativações — sempre inteiro, então não faz
        // sentido o echarts gerar ticks fracionários (0, 0.5, 1...).
        minInterval: 1,
        axisLabel: { color: '#999', fontSize: 11 },
        splitLine: { lineStyle: { color: '#ECE9F8' } }
      },
      yAxis: {
        type: 'category',
        data: data.map(item => item.subject),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#5D6A86', fontSize: 12 }
      },
      series: [
        {
          name: 'Acerto',
          type: 'bar',
          stack: 'total',
          data: data.map(item => item.correct),
          itemStyle: { color: CORRECT_COLOR },
          barMaxWidth: 22
        },
        {
          name: 'Erro',
          type: 'bar',
          stack: 'total',
          data: data.map(item => item.incorrect),
          itemStyle: { color: INCORRECT_COLOR },
          barMaxWidth: 22
        }
      ]
    });
  }, [data]);

  if (!data.length) {
    return (
      <EmptyState
        type="data"
        variant="plain"
        text="Nenhum dado disponível"
      />
    );
  }

  return (
    <ReactECharts
      option={option}
      echarts={echarts}
      style={{ height: Math.max(320, data.length * 42), width: '100%' }}
      notMerge={true}
    />
  );
}
